import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { mdiThemeLightDark, mdiWeatherNight, mdiWeatherSunny } from "@mdi/js";
import type { HabMenuItem } from "@livereader/habitus-vue";
import { useAppTheme, type ThemeMode } from "@/shared/composables/useAppTheme";

/**
 * The theme choice as HabMenu rows: light, dark, or follow the system.
 *
 * One definition for every place that offers it — AppShell's user menu and the landing
 * top bar — so the two cannot come to disagree on the order, the icons or the words.
 * It replaced `useAppTheme().themeIcon`, which handed out `mdi-` names for Vuetify to
 * look up; these are `@mdi/js` path data, which is what HabMenu and HabButton draw.
 */
const MODE_ICONS: Record<ThemeMode, string> = {
	light: mdiWeatherSunny,
	dark: mdiWeatherNight,
	system: mdiThemeLightDark,
};

export function useAppearanceMenu() {
	const { t } = useI18n();
	const { themeMode, setThemeMode } = useAppTheme();

	const items = computed<HabMenuItem[]>(() =>
		(Object.keys(MODE_ICONS) as ThemeMode[]).map((mode) => ({
			label: t(`theme.${mode}`),
			icon: MODE_ICONS[mode],
			selected: themeMode.value === mode,
			onSelect: () => setThemeMode(mode),
		})),
	);

	/** The chosen mode's icon, for a trigger that shows what is selected. */
	const icon = computed(() => MODE_ICONS[themeMode.value]);

	return { items, icon };
}
